import {
  quitDayExpenseId,
  quitPlanId,
  type CravingLike,
  type DateRange,
  type LocalDate,
  type Price,
  type QuitPlan,
  type SmokeKind,
  type SmokeLogLike,
  type TaperSegment,
} from '@tahi/core';
import { cravings, ledgerEntries, quitPlans, smokeLogs } from '@tahi/db';
import { and, asc, eq, gte, isNull, lte } from 'drizzle-orm';
import { TODAY, type TileRow } from '../../tiles/layout';
import type { LocalDb } from '../localDb';
import { listTiles } from './tiles';

type PlanRow = typeof quitPlans.$inferSelect;
type LogRow = typeof smokeLogs.$inferSelect;
type CravingRow = typeof cravings.$inferSelect;

export interface SmokeLogRow extends SmokeLogLike {
  recordedTz: string;
}

export interface DayExpenseRow {
  id: string;
  amountMinor: number;
  fundId: string | null;
  deletedAt: string | null;
}

function toPrice(r: PlanRow): Price {
  if (r.priceKind === 'pack' && r.packPriceMinor && r.perPack) {
    return { kind: 'pack', packPriceMinor: r.packPriceMinor, perPack: r.perPack };
  }
  if (r.priceKind === 'stick' && r.stickPriceMinor) {
    return { kind: 'stick', stickPriceMinor: r.stickPriceMinor };
  }
  return { kind: 'none' };
}

export function toPlan(r: PlanRow): QuitPlan {
  return {
    id: r.id,
    baselinePerDay: r.baselinePerDay,
    startDate: r.startDate,
    taper: (r.taper ?? []) as TaperSegment[],
    price: toPrice(r),
    fundId: r.fundId ?? null,
    triggerPlans: r.triggerPlans ?? {},
    quitlineCountry: r.quitlineCountry ?? null,
  };
}

function toLog(r: LogRow): SmokeLogRow {
  return {
    id: r.id,
    at: r.at,
    date: r.date,
    kind: r.kind as SmokeKind,
    count: r.count,
    costMinor: r.costMinor,
    trigger: r.trigger ?? null,
    deletedAt: r.deletedAt ?? null,
    recordedTz: r.recordedTz,
  };
}

function toCraving(r: CravingRow): CravingLike {
  return {
    id: r.id,
    at: r.at,
    date: r.date,
    resisted: Boolean(r.resisted),
    trigger: r.trigger ?? null,
    delayUsed: Boolean(r.delayUsed),
    deletedAt: r.deletedAt ?? null,
  };
}

/** The live plan, or null before setup. */
export async function getPlan(db: LocalDb, userId: string): Promise<QuitPlan | null> {
  const r = await db
    .select()
    .from(quitPlans)
    .where(and(eq(quitPlans.userId, userId), eq(quitPlans.id, quitPlanId(userId))))
    .get();
  return r && !r.deletedAt ? toPlan(r) : null;
}

/** Live smoke logs, oldest first; all of them when no range is given (money "all time"). */
export async function listSmokeLogs(
  db: LocalDb,
  userId: string,
  range?: DateRange,
): Promise<SmokeLogRow[]> {
  const conds = [eq(smokeLogs.userId, userId), isNull(smokeLogs.deletedAt)];
  if (range) conds.push(gte(smokeLogs.date, range.start), lte(smokeLogs.date, range.end));
  const rows = await db
    .select()
    .from(smokeLogs)
    .where(and(...conds))
    .orderBy(asc(smokeLogs.at))
    .all();
  return rows.map(toLog);
}

/** One day's logs, oldest first. Undo of a delete needs the deleted ones too. */
export async function listDayLogs(
  db: LocalDb,
  userId: string,
  date: LocalDate,
  opts: { includeDeleted?: boolean } = {},
): Promise<SmokeLogRow[]> {
  const conds = [eq(smokeLogs.userId, userId), eq(smokeLogs.date, date)];
  if (!opts.includeDeleted) conds.push(isNull(smokeLogs.deletedAt));
  const rows = await db
    .select()
    .from(smokeLogs)
    .where(and(...conds))
    .orderBy(asc(smokeLogs.at))
    .all();
  return rows.map(toLog);
}

/** One log, deleted or not. */
export async function getSmokeLog(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<SmokeLogRow | null> {
  const r = await db
    .select()
    .from(smokeLogs)
    .where(and(eq(smokeLogs.userId, userId), eq(smokeLogs.id, id)))
    .get();
  return r ? toLog(r) : null;
}

/** Live cravings in the range, oldest first. */
export async function listCravings(
  db: LocalDb,
  userId: string,
  range: DateRange,
): Promise<CravingLike[]> {
  const rows = await db
    .select()
    .from(cravings)
    .where(
      and(
        eq(cravings.userId, userId),
        isNull(cravings.deletedAt),
        gte(cravings.date, range.start),
        lte(cravings.date, range.end),
      ),
    )
    .orderBy(asc(cravings.at))
    .all();
  return rows.map(toCraving);
}

/** The day's Cigarettes expense row, deleted or not. */
export async function getDayExpense(
  db: LocalDb,
  userId: string,
  date: LocalDate,
): Promise<DayExpenseRow | null> {
  const r = await db
    .select({
      id: ledgerEntries.id,
      amountMinor: ledgerEntries.amountMinor,
      fundId: ledgerEntries.fundId,
      deletedAt: ledgerEntries.deletedAt,
    })
    .from(ledgerEntries)
    .where(
      and(eq(ledgerEntries.userId, userId), eq(ledgerEntries.id, quitDayExpenseId(userId, date))),
    )
    .get();
  return r
    ? {
        id: r.id,
        amountMinor: r.amountMinor,
        fundId: r.fundId ?? null,
        deletedAt: r.deletedAt ?? null,
      }
    : null;
}

/** Quit Smoking tiles placed on Today (live rows, hidden or not). */
export async function listQuitTodayTiles(db: LocalDb): Promise<TileRow[]> {
  const rows = await listTiles(db, TODAY);
  return rows.filter((t) => t.moduleId === 'quit_smoking' && !t.deletedAt);
}
